import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DollarSign, Building2, Download, PiggyBank, TrendingDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { calculateEmployeeTaxes } from '@/components/utils/dynamicTaxCalculations';
import { downloadCSV } from '@/components/utils/pdfGeneration';
import { format } from 'date-fns';

export default function TaxSavingsReports() {
  const [employers, setEmployers] = useState([]);
  const [selectedEmployer, setSelectedEmployer] = useState('all');
  const [employees, setEmployees] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [savingsData, setSavingsData] = useState([]);
  const [aggregateStats, setAggregateStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Re-run once employees/enrollments actually finish loading (loadData
    // resolves asynchronously after mount), not just when selectedEmployer
    // changes — previously this only fired once on mount, before loadData
    // had populated either array, so it always computed against empty data.
    if (selectedEmployer) {
      calculateSavings();
    }
  }, [selectedEmployer, employees, enrollments]);

  const loadData = async () => {
    try {
      const [employersData, employeesData, enrollmentsData] = await Promise.all([
        api.entities.Employer.list(),
        api.entities.Employee.list(),
        api.entities.Enrollment.filter({ status: 'active' })
      ]);
      setEmployers(employersData);
      setEmployees(employeesData);
      setEnrollments(enrollmentsData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const calculateSavings = async () => {
    setCalculating(true);
    try {
      const filtered = selectedEmployer === 'all'
        ? employees
        : employees.filter(e => e.employer_id === selectedEmployer);

      const savingsPromises = filtered.map(async (employee) => {
        const empEnrollments = enrollments.filter(e => e.employee_id === employee.id);
        const totalPremiums = empEnrollments.reduce((sum, e) => sum + (e.monthly_premium || 0), 0);

        if (totalPremiums > 0 && employee.annual_salary > 0) {
          try {
            const taxAnalysis = await calculateEmployeeTaxes(employee, totalPremiums * 12);
            return {
              employeeId: employee.id,
              employeeName: `${employee.first_name} ${employee.last_name}`,
              annualSalary: employee.annual_salary,
              annualPremiums: totalPremiums * 12,
              federalSavings: taxAnalysis.savings.federal,
              stateSavings: taxAnalysis.savings.state,
              ficaSavings: taxAnalysis.savings.fica,
              totalSavings: taxAnalysis.savings.total,
              monthlySavings: taxAnalysis.savings.monthlySavings,
              employerFICASavings: (totalPremiums * 12) * 0.0765 // Employer also saves FICA
            };
          } catch {
            return null;
          }
        }
        return null;
      });

      const results = (await Promise.all(savingsPromises)).filter(r => r !== null);
      setSavingsData(results);

      // Calculate aggregates
      const totalEmployeeSavings = results.reduce((sum, r) => sum + r.totalSavings, 0);
      const totalEmployerSavings = results.reduce((sum, r) => sum + r.employerFICASavings, 0);
      const avgEmployeeSavings = results.length > 0 ? totalEmployeeSavings / results.length : 0;
      const totalFederalSavings = results.reduce((sum, r) => sum + r.federalSavings, 0);
      const totalStateSavings = results.reduce((sum, r) => sum + r.stateSavings, 0);
      const totalFICASavings = results.reduce((sum, r) => sum + r.ficaSavings, 0);

      setAggregateStats({
        totalEmployeeSavings,
        totalEmployerSavings,
        combinedSavings: totalEmployeeSavings + totalEmployerSavings,
        avgEmployeeSavings,
        totalFederalSavings,
        totalStateSavings,
        totalFICASavings,
        employeesWithSavings: results.length
      });
    } catch (err) {
      console.error(err);
    } finally {
      setCalculating(false);
    }
  };

  const exportSavings = () => {
    const rows = [['Employee Name', 'Annual Salary', 'Annual Premiums', 'Federal Savings', 'State Savings', 'FICA Savings', 'Total Savings', 'Employer FICA Savings']];
    savingsData.forEach(s => {
      rows.push([
        s.employeeName,
        s.annualSalary.toFixed(2),
        s.annualPremiums.toFixed(2),
        s.federalSavings.toFixed(2),
        s.stateSavings.toFixed(2),
        s.ficaSavings.toFixed(2),
        s.totalSavings.toFixed(2),
        s.employerFICASavings.toFixed(2)
      ]);
    });

    const csv = rows.map(row => row.join(',')).join('\n');
    downloadCSV(csv, `tax_savings_report_${format(new Date(), 'yyyy-MM-dd')}.csv`);
  };

  const topSavers = [...savingsData].sort((a, b) => b.totalSavings - a.totalSavings).slice(0, 10);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Tax Savings Reports</h1>
          <p className="text-slate-500 mt-1">
            Section 125 tax savings analysis and ROI
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Select value={selectedEmployer} onValueChange={setSelectedEmployer}>
            <SelectTrigger className="w-full sm:w-48">
              <Building2 className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Employers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Employers</SelectItem>
              {employers.map(emp => (
                <SelectItem key={emp.id} value={emp.id}>{emp.company_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={exportSavings} variant="outline" disabled={savingsData.length === 0}>
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {calculating && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="animate-spin w-5 h-5 border-4 border-blue-500 border-t-transparent rounded-full" />
            <p className="text-blue-900">Calculating tax savings for all employees...</p>
          </CardContent>
        </Card>
      )}

      {/* Aggregate Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <DollarSign className="w-6 h-6 sm:w-8 sm:h-8 opacity-80 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-emerald-100 text-xs sm:text-sm">Total Employee Savings</p>
                <p className="text-lg sm:text-2xl font-bold truncate">${(aggregateStats.totalEmployeeSavings || 0).toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <Building2 className="w-6 h-6 sm:w-8 sm:h-8 opacity-80 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-blue-100 text-xs sm:text-sm">Employer FICA Savings</p>
                <p className="text-lg sm:text-2xl font-bold truncate">${(aggregateStats.totalEmployerSavings || 0).toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <PiggyBank className="w-6 h-6 sm:w-8 sm:h-8 opacity-80 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-purple-100 text-xs sm:text-sm">Combined Savings</p>
                <p className="text-lg sm:text-2xl font-bold truncate">${(aggregateStats.combinedSavings || 0).toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <TrendingDown className="w-6 h-6 sm:w-8 sm:h-8 opacity-80 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-amber-100 text-xs sm:text-sm">Avg Per Employee</p>
                <p className="text-lg sm:text-2xl font-bold truncate">${(aggregateStats.avgEmployeeSavings || 0).toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Savings Breakdown Chart */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <Card>
          <CardHeader>
            <CardTitle>Tax Savings Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={[
                { name: 'Federal Tax', value: aggregateStats.totalFederalSavings || 0 },
                { name: 'State Tax', value: aggregateStats.totalStateSavings || 0 },
                { name: 'FICA', value: aggregateStats.totalFICASavings || 0 }
              ]}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                <Bar dataKey="value" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </motion.div>

      {/* Top Savers Table */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <Card>
          <CardHeader>
            <CardTitle>Top 10 Tax Savers</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Annual Salary</TableHead>
                  <TableHead>Annual Premiums</TableHead>
                  <TableHead>Federal</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>FICA</TableHead>
                  <TableHead>Total Savings</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topSavers.map((emp) => (
                  <TableRow key={emp.employeeId}>
                    <TableCell className="font-medium">{emp.employeeName}</TableCell>
                    <TableCell>${emp.annualSalary.toLocaleString()}</TableCell>
                    <TableCell>${emp.annualPremiums.toLocaleString()}</TableCell>
                    <TableCell>${emp.federalSavings.toFixed(0)}</TableCell>
                    <TableCell>${emp.stateSavings.toFixed(0)}</TableCell>
                    <TableCell>${emp.ficaSavings.toFixed(0)}</TableCell>
                    <TableCell className="font-bold text-emerald-600">
                      ${emp.totalSavings.toFixed(0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {topSavers.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                No savings data available
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* ROI Summary */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
        <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
          <CardContent className="p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-emerald-900 mb-4">Section 125 Program ROI</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              <div>
                <p className="text-sm text-emerald-700">Employees Saving</p>
                <p className="text-2xl sm:text-3xl font-bold text-emerald-900">{aggregateStats.employeesWithSavings || 0}</p>
              </div>
              <div>
                <p className="text-sm text-emerald-700">Total Annual Employee Savings</p>
                <p className="text-2xl sm:text-3xl font-bold text-emerald-900">
                  ${(aggregateStats.totalEmployeeSavings || 0).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-emerald-700">Total Employer FICA Savings</p>
                <p className="text-2xl sm:text-3xl font-bold text-emerald-900">
                  ${(aggregateStats.totalEmployerSavings || 0).toLocaleString()}
                </p>
              </div>
            </div>
            <div className="mt-4 sm:mt-6 p-4 bg-white rounded-lg">
              <p className="text-emerald-800">
                <strong>ðŸ’¡ Program Impact:</strong> Through Section 125 pre-tax benefits, your organization is saving 
                <strong className="text-emerald-600"> ${(aggregateStats.combinedSavings || 0).toLocaleString()} annually</strong> in combined 
                employee and employer tax savings.
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}